// HOOKS
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/react-hooks';


// HELPERS
import withData from 'lib/apollo';
import {_} from 'locale';
import fetch from 'isomorphic-unfetch';
import classNames from 'classnames';
import {getDataWithLanguage} from 'lib/i18n';

// COMPONENTS
import {Formik, Form, Field} from 'formik';

import Error404 from '../error-404';
import * as Yup from 'yup';
import {SimpleField, FileField, SuccessMessage, createSubmitHandler} from 'lib/forms';

// STYLES
import styles from './styles.css';
import formStyles from 'lib/forms/styles.css';
import layoutStyles from '../../../styles/layout.css';

// QUERIES
import {getCallTitle} from './queries.gql';

const HAU = (props) => {
    const {query} = useRouter();

    if(isNaN(Number(query.id))) return <Error404 />;

    const {loading, error, data} = useQuery(getCallTitle, {variables: {where: {
        id: query.id,
        form_type:"HAU",
    }}});

    if (loading) return '';
    if (error) throw {statusCode: 404};
    if (!data || !data.calls || !data.calls.length) throw {statusCode: 404};

    const {title} = getDataWithLanguage(data.calls[0], query.language);

    // TODO: Add check for selected form type on relevant Call.

    const validationSchema = Yup.object().shape({
        is_person: Yup.boolean().required(),
        first_name: Yup.string().trim().required(),
        last_name: Yup.string().trim().required(),
        national_id: Yup.string().required(),
        birthdate: Yup.date().required(),
        nationality: Yup.string().required(),
        phone: Yup.number().required(),
        email: Yup.string().email().required(),
        website: Yup.string(),
        address: Yup.string().required(),
        zip: Yup.string().required(),
        city: Yup.string().required(),
        state: Yup.string().required(),
        country: Yup.string().required(),
        report: Yup.mixed()
            .required()
            .test('file type', '', value => !value || value.type == 'application/pdf')
            .test('file type', '', value => !value || value.size < (3 * 1024 * 1024)),
        presentation: Yup.mixed()
            .required()
            .test('file type', '', value => !value || value.type == 'application/pdf')
            .test('file type', '', value => !value || value.size < (3 * 1024 * 1024)),
        link: Yup.string(),
        cv: Yup.string().max(2000).required(),
        id_proof: Yup.mixed()
            .required()
            .test('file type', '', value => !value || value.type.match(/(jpeg|jpg|pdf|gif)/i))
            .test('file type', '', value => !value || value.size < (3 * 1024 * 1024)),
        tc: Yup.boolean().oneOf([true]),
        legal: Yup.boolean().oneOf([true]),
        call: Yup.number().required(),
    })

    const initialValues = {
        is_person: null,
        first_name: '',
        last_name: '',
        national_id: '',
        birthdate: '',
        nationality: '',
        phone: '',
        email: '',
        website: '',
        address: '',
        zip: '',
        city: '',
        state: '',
        country: '',
        report: null,
        presentation: null,
        link: '',
        cv: '',
        id_proof: null,
        tc: false,
        legal: false,
        call: Number(query.id),
    }

    const onSubmit = createSubmitHandler('/api/haus');

    return (
        <section className={layoutStyles.container}>
            <div className={layoutStyles.content}>
                <h2 className={classNames(layoutStyles.h2, styles.h2)}>
                    {_('FORMULARIO DE INSCRIPCIÓN')} - {title.toUpperCase()}
                </h2>
                <Formik validateOnChange={false} validateOnBlur={false} {...{validationSchema, initialValues, onSubmit}}>
                    {formProps => {
                        const {handleSubmit, setFieldValue} = formProps;

                        if (formProps.status && formProps.status.success) {
                            return (
                                <SuccessMessage
                                    next={`/calls/${query.id}`}
                                    message={formProps.status.success}
                                />
                            );
                        }

                        return (
                            <Form onSubmit={handleSubmit}>
                                <h3 className={layoutStyles.h3}>{_('DATOS PERSONALES')}</h3>
                                <Field name='is_person'>
                                    {({field, meta}) => (
                                        <div className={classNames(styles.grid, styles.data)}>
                                            <div className={formStyles.radio}>
                                                <label>
                                                    <input type="radio" name="is_person" {...field} value={true}/>
                                                    <span className={formStyles.labelVisible}><span className={formStyles.fakeRadio}></span></span>
                                                    <span>{_('Persona física')}</span>
                                                </label>
                                            </div>
                                            <div className={formStyles.radio}>
                                                <label>
                                                    <input type="radio" name="is_person" {...field} value={false}/>
                                                    <span className={formStyles.labelVisible}><span className={formStyles.fakeRadio}></span></span>
                                                    <span>{_('Colectivo')}</span>
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                </Field>
                                <span>{_('En caso de ser un colectivo, rellene los campos con los datos de la representante.')}</span>

                                <div className={formStyles.grid}>
                                    <SimpleField name='first_name' label={_('Nombre')}/>
                                    <SimpleField name='last_name' label={_('Apellidos')}/>
                                    <SimpleField name='national_id' label={_('DNI')}/>
                                    <SimpleField name='birthdate' label={_('Fecha de nacimiento')} type="date"/>
                                    <SimpleField name='nationality' label={_('Nacionalidad')}/>
                                    <SimpleField name='phone' label={_('Teléfono')} type="number"/>
                                    <SimpleField name='email' label={_('Correo Electrónico')}/>
                                    <SimpleField name='website' label={_('Página web')}/>
                                </div>

                                <h3 className={layoutStyles.h3}>{_('DIRECCIÓN')}</h3>
                                <div className={formStyles.grid}>
                                    <SimpleField name='address' label={_('Dirección')}/>
                                    <SimpleField name='zip' label={_('Código postal')}/>
                                    <SimpleField name='city' label={_('Ciudad')}/>
                                    <SimpleField name='state' label={_('Provincia')}/>
                                    <SimpleField name='country' label={_('País')}/>
                                </div>


                                <h3 className={layoutStyles.h3}>{_('DOCUMENTACIÓN')}</h3>
                                <div className={formStyles.field}>
                                    <span>
                                        {_('Memoria del proyecto:')}
                                        <br />
                                        {_('(PDF máx. 20MB)')}
                                    </span>
                                    <FileField name='report' setFieldValue={setFieldValue} />
                                </div>
                                <div className={formStyles.field}>
                                    <span>
                                        {_('Panel de presentación')}
                                        <br />
                                        {_('(PDF máx. 15MB)')}
                                    </span>
                                    <FileField name='presentation' setFieldValue={setFieldValue} />
                                </div>
                                <div className={formStyles.field}>
                                    <SimpleField name='link' label={_('Enlace a imágenes en alta resolución')} />
                                </div>
                                <div className={formStyles.field}>
                                    <label>
                                        <div>{_('Curriculum Vitae (2000 caracteres máx.)')}</div>
                                    </label>
                                    <SimpleField name='cv' type='textarea' />
                                </div>
                                <div className={formStyles.field}>
                                    <span>
                                        {_('Copia de un documento de identidad del/la candidato/a')}
                                        <br />
                                        {_('(JPEG / JPG / PDF / GIF máx. 2MB)')}
                                    </span>
                                    <FileField name='id_proof' setFieldValue={setFieldValue} />
                                </div>

                                <div className={classNames(styles.field, styles.small)}>
                                    <div className={formStyles.checkbox}>
                                        <SimpleField name='tc' type='checkbox'>
                                            <span>{_('He leído y acepto las presentes Bases y las decisiones del Jurado en cualquiera de las fases.')}</span>
                                        </SimpleField>
                                    </div>
                                    <div className={formStyles.checkbox}>
                                        <SimpleField name='legal' type='checkbox'>
                                            <span>{_("Al rellenar los datos de esta solicitud de Bitamine, me considero informado/a y acepto la POLÍTICA DE PRIVACIDAD y el AVISO LEGAL que existen en el sitio web www.bitamine.net y doy consentimiento para que trate mis datos de carácter personal en un fichero automatizado con el objeto de que se puedan comunicarse conmigo en el marco de esta convocatoria. Estoy informado/a de que puedo ejercitar mis derechos poniéndome en contacto con la persona delegada de protección de datos de la asociación por lo que respecta a todas las cuestiones relativas al tratamiento de sus datos personales y al ejercicio de sus derechos.")}</span>
                                        </SimpleField>
                                    </div>
                                </div>
                                <div className={formStyles.submit}>
                                    <button
                                        type="submit"
                                        disabled={formProps.isSubmitting}
                                        className={classNames(layoutStyles.buttonPrimary, layoutStyles.fill)}>
                                        {_('Enviar')}
                                    </button>
                                    {((!formProps.isValid && formProps.submitCount > 0) || formProps.status && formProps.status.error)  && (
                                        <div className={formStyles.errorMessage}>
                                            {formProps.status && formProps.status.error
                                                ? formProps.status && formProps.status.error
                                                :  _('Por favor, corrige los errores del formulario.')
                                            }
                                        </div>
                                    )}
                                    {formProps.status && formProps.status.success && (
                                        <div className={formStyles.successMessage}>
                                            {formProps.status.success}
                                        </div>
                                    )}
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
    </section>
    )
}


export default withData(HAU)
